import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";
import { safeJsonStringify } from "@/lib/utils";

interface SEOImageProps {
  url: string;
  width?: number;
  height?: number;
  alt: string;
  type?: string;
}

interface EnhancedSEOProps {
  title: string;
  description: string;
  keywords: string[];
  canonicalUrl: string;
  ogType?: "website" | "article" | "profile" | "product" | "service";
  image?: SEOImageProps;
  createdAt?: string;
  updatedAt?: string;
  noIndex?: boolean;
  author?: {
    name: string;
    url?: string;
  };
  organization?: {
    name: string;
    logo: string;
    url: string;
    sameAs?: string[];
  };
  breadcrumbs?: Array<{
    name: string;
    url: string;
    position: number;
  }>;
  schema?: {
    type: string;
    data: Record<string, any>;
  };
  preloadAssets?: string[];
  securityHeaders?: boolean;
  performance?: {
    preconnect?: string[];
    prefetch?: string[];
    prerender?: string[];
  };
  children?: React.ReactNode;
}

export const EnhancedSEO = ({
  title,
  description,
  keywords,
  canonicalUrl,
  ogType = "website",
  image,
  createdAt,
  updatedAt,
  noIndex = false,
  author,
  organization = {
    name: "edgefi",
    logo: "/lovable-uploads/3faf62f0-dd43-43cc-82c2-9329f89d269f.png",
    url: "https://edgefi.com",
    sameAs: [
      "https://twitter.com/edgefi",
      "https://www.linkedin.com/company/edgefi",
      "https://www.facebook.com/edgefi"
    ]
  },
  breadcrumbs,
  schema,
  preloadAssets = [],
  securityHeaders = true,
  performance = {
    preconnect: ["https://fonts.googleapis.com", "https://fonts.gstatic.com"],
    prefetch: [],
    prerender: []
  },
  children
}: EnhancedSEOProps) => {
  const location = useLocation();
  
  // Format the title to keep it under 60 characters
  const formattedTitle = title.length > 60 
    ? `${title.substring(0, 57)}...` 
    : title;
  
  // Format the description to keep it under 155 characters
  const formattedDescription = description.length > 155 
    ? `${description.substring(0, 152)}...` 
    : description;
  
  // Build the full canonicalUrl
  const fullCanonicalUrl = canonicalUrl.startsWith('http') 
    ? canonicalUrl 
    : `https://edgefi.com${canonicalUrl.startsWith('/') ? canonicalUrl : `/${canonicalUrl}`}`;
  
  // Default image if none provided
  const defaultImage = {
    url: "https://edgefi.com/og-image.png",
    width: 1200,
    height: 630,
    alt: "edgefi - Cybersecurity designed to evolve with the times",
    type: "image/png"
  };
  
  const seoImage = image ? { ...defaultImage, ...image } : defaultImage;
  
  // Base schema for the organization
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": organization.name,
    "url": organization.url,
    "logo": {
      "@type": "ImageObject",
      "url": organization.logo.startsWith('http') ? organization.logo : `https://edgefi.com${organization.logo}`
    },
    "sameAs": organization.sameAs
  };
  
  // Generate breadcrumb schema if breadcrumbs are provided
  const breadcrumbSchema = breadcrumbs ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map(crumb => ({
      "@type": "ListItem",
      "position": crumb.position,
      "name": crumb.name,
      "item": crumb.url.startsWith('http') ? crumb.url : `https://edgefi.com${crumb.url}`
    }))
  } : null;
  
  // Generate schema based on content type
  const getContentSchema = () => {
    if (!schema) return null;
    
    // If custom schema is provided, use it
    return {
      "@context": "https://schema.org",
      ...schema.data
    };
  };
  
  const contentSchema = getContentSchema();
  
  // Check if we're in the admin section to disable security headers
  const isAdminRoute = location.pathname.startsWith('/admin');

  // Define relaxed CSP for admin routes
  const adminCSP = `
    default-src 'self';
    script-src 'self' 'unsafe-inline' 'unsafe-eval' https://cdn.gpteng.co https://*.supabase.co https://*.supabase.in https://login.microsoftonline.com https://api.amplitude.com https://bat.bing.com https://*.datadoghq.com https://*.zoominfo.com;
    style-src 'self' 'unsafe-inline' https://fonts.googleapis.com;
    img-src 'self' data: blob: https://*.supabase.co https://*.supabaseusercontent.com https://*.githubusercontent.com https://login.microsoftonline.com https://*.microsoft.com;
    font-src 'self' https://fonts.gstatic.com;
    connect-src 'self' https://*.supabase.co https://*.supabase.in https://login.microsoftonline.com https://api.amplitude.com https://bat.bing.com https://*.datadoghq.com https://*.zoominfo.com;
    frame-src 'self' https://login.microsoftonline.com;
    worker-src 'self' blob:;
  `;
  
  // Regular CSP for other routes
  const regularCSP = "upgrade-insecure-requests";
  
  return (
    <Helmet>
      {/* Basic meta tags */}
      <title>{formattedTitle}</title>
      <meta name="description" content={formattedDescription} />
      {keywords.length > 0 && (
        <meta name="keywords" content={keywords.join(", ")} />
      )}
      <link rel="canonical" href={fullCanonicalUrl} />
      
      {/* Index control - now default to index */}
      <meta name="robots" content={noIndex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"} />
      
      {/* OpenGraph meta tags */}
      <meta property="og:title" content={formattedTitle} />
      <meta property="og:description" content={formattedDescription} />
      <meta property="og:url" content={fullCanonicalUrl} />
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content="edgefi" />
      <meta property="og:image" content={seoImage.url} />
      <meta property="og:image:width" content={String(seoImage.width)} />
      <meta property="og:image:height" content={String(seoImage.height)} />
      <meta property="og:image:alt" content={seoImage.alt} />
      <meta property="og:locale" content="en_US" />
      
      {/* Article specific tags */}
      {ogType === "article" && author && (
        <>
          <meta property="article:author" content={author.url || fullCanonicalUrl} />
          {createdAt && <meta property="article:published_time" content={createdAt} />}
          {updatedAt && <meta property="article:modified_time" content={updatedAt} />}
        </>
      )}
      
      {/* Twitter Card tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={formattedTitle} />
      <meta name="twitter:description" content={formattedDescription} />
      <meta name="twitter:image" content={seoImage.url} />
      <meta name="twitter:image:alt" content={seoImage.alt} />
      
      {/* JSON-LD schemas */}
      {organizationSchema && (
        <script type="application/ld+json">{safeJsonStringify(organizationSchema)}</script>
      )}
      {breadcrumbSchema && (
        <script type="application/ld+json">{safeJsonStringify(breadcrumbSchema)}</script>
      )}
      {contentSchema && (
        <script type="application/ld+json">{safeJsonStringify(contentSchema)}</script>
      )}
      
      {/* Additional tags */}
      <meta name="author" content={author?.name || "edgefi"} />
      
      {/* Performance optimizations - preconnect */}
      {performance.preconnect?.map((url, index) => (
        <link key={`preconnect-${index}`} rel="preconnect" href={url} crossOrigin="anonymous" />
      ))}
      
      {/* Performance optimizations - prefetch */}
      {performance.prefetch?.map((url, index) => (
        <link key={`prefetch-${index}`} rel="prefetch" href={url} />
      ))}
      
      {/* Performance optimizations - prerender */}
      {performance.prerender?.map((url, index) => (
        <link key={`prerender-${index}`} rel="prerender" href={url} />
      ))}
      
      {/* Preload critical assets */}
      {preloadAssets.map((asset, index) => {
        // Determine asset type
        let as = "image";
        if (asset.endsWith(".js")) as = "script";
        if (asset.endsWith(".css")) as = "style";
        if (asset.endsWith(".woff2") || asset.endsWith(".woff")) as = "font";
        
        return (
          <link 
            key={`preload-${index}`} 
            rel="preload" 
            href={asset} 
            as={as} 
            crossOrigin={as === "font" ? "anonymous" : undefined} 
          />
        );
      })}
      
      {/* Security headers - use different CSP for admin routes */}
      {securityHeaders && (
        <>
          {isAdminRoute ? (
            <meta http-equiv="Content-Security-Policy" content={adminCSP} />
          ) : (
            <meta http-equiv="Content-Security-Policy" content={regularCSP} />
          )}
          <meta http-equiv="X-XSS-Protection" content="1; mode=block" />
          <meta http-equiv="X-Content-Type-Options" content="nosniff" />
          <meta http-equiv="Referrer-Policy" content="strict-origin-when-cross-origin" />
        </>
      )}
      
      {/* Children for additional custom meta tags */}
      {children}
    </Helmet>
  );
};
